import {
  Flex,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Pill,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { AssessmentPlanService } from "../../../../services/AssessmentService";
import { TermsContext } from "../../../../utils/Context";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import { isArchived, sortOutcomes } from "../../../LearningOutcomes/utils";
import { MeasureForm } from "../ComponentForms/MeasureForm";
import { ExternalDetails } from "../Details/ExternalDetails";
import { JADetails } from "../Details/JADetails";
import { CANVAS } from "../utils";

const AssessmentCard = ({ assessment, inTemplate, lastMeasure }) => {
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const {
    sectionIndex,
    measureIndex,
    handleDeleteMeasure,
    setSections,
    extraData,
  } = useContext(ApDataContext);
  const showNotification = useNotification();
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [appliedTerms, setAppliedTerms] = useState([]);
  const assignmentType = extraData.assignmentTypes.find(
    (type) => type.id === assessment.assignment_type
  );
  const relevantOutcomes = assessment.los.filter(
    (lo) => assessment.deployed || !isArchived(lo)
  );

  useEffect(() => {
    if (canvasTerms.length > 0 && insightsTerms.length > 0) {
      if (assessment.assessment_type === "external") {
        const canvasTerm = canvasTerms.find(
          (t) => t.id === assessment.assessing_terms[0]
        );
        if (canvasTerm) {
          setAppliedTerms([
            insightsTerms.find((t) => t.id === canvasTerm.insights_term_id),
          ]);
        }
      } else {
        const selectedTerms = [];
        assessment.terms.forEach((term) => {
          if (!selectedTerms.includes(term.insights_term_id)) {
            selectedTerms.push(term.insights_term_id);
          }
        });
        setAppliedTerms(
          insightsTerms.filter((term) => selectedTerms.includes(term.id))
        );
      }
    }
  }, [insightsTerms, canvasTerms, assessment]);

  const AssessmentPill = () => {
    return (
      <Pill color="info" themeOverride={{ maxWidth: "30rem" }}>
        {t(
          assessment.assessment_type === "external"
            ? assessment.purl_type !== "juried"
              ? "assessment.externalAssessment"
              : "assessment.externalJuriedAssessment"
            : assessment.collecting_evidence
              ? "assessment.canvasJuriedAssessment"
              : assessment.assessment_type === "flexible"
                ? "results.flexibleCanvasAssessment"
                : "results.targetedCanvasAssessment"
        )}
      </Pill>
    );
  };

  const handleSetFilesApprover = async (email) => {
    let success = true;
    await AssessmentPlanService.updateAssessmentEvidenceApprover(
      email,
      extraData.plan.id,
      assessment.id
    )
      .then((res) => {
        setSections((prevSections) => {
          const newSections = [...prevSections];
          newSections[sectionIndex].measures[measureIndex].evidence_approver =
            res.evidence_approver;
          return newSections;
        });
      })
      .catch(async (e) => {
        success = false;
        const res = await e.json();
        if (res === t("notifications.evidenceApproval.userNotFound")) {
          showNotification(
            t("notifications.evidenceApproval.userNotFound"),
            ERROR
          );
        } else if (
          res === t("notifications.evidenceApproval.userNoPermission")
        ) {
          showNotification(
            t("notifications.evidenceApproval.userNoPermission"),
            ERROR
          );
        } else {
          showNotification(t("notifications.evidenceApproval.error"), ERROR);
        }
      });
    return success;
  };

  const moveMeasureUp = () => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              measures: section.measures.map((m, j) =>
                j === measureIndex
                  ? section.measures[measureIndex - 1]
                  : j === measureIndex - 1
                    ? section.measures[measureIndex]
                    : m
              ),
            }
          : section
      )
    );
  };

  const moveMeasureDown = () => {
    setSections((prev) =>
      prev.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              measures: section.measures.map((m, j) =>
                j === measureIndex
                  ? section.measures[measureIndex + 1]
                  : j === measureIndex + 1
                    ? section.measures[measureIndex]
                    : m
              ),
            }
          : section
      )
    );
  };

  return (
    <>
      <Card style={{ backgroundColor: "#FFFFFF" }}>
        <Flex wrap="wrap" alignItems="start" gap="small">
          <Flex.Item>
            <AssessmentPill />
          </Flex.Item>
          <Flex.Item shouldGrow textAlign="end">
            {!inTemplate && (
              <Tooltip
                renderTip={t("measures.moveMeasureUp")}
                on={["hover", "focus"]}
              >
                <IconButton
                  renderIcon={IconArrowOpenUpLine}
                  screenReaderLabel={t("measures.moveMeasureUp")}
                  size="small"
                  onClick={moveMeasureUp}
                  disabled={measureIndex === 0}
                />
              </Tooltip>
            )}
            {!inTemplate && (
              <Tooltip
                renderTip={t("measures.moveMeasureDown")}
                on={["hover", "focus"]}
              >
                <IconButton
                  renderIcon={IconArrowOpenDownLine}
                  screenReaderLabel={t("measures.moveMeasureDown")}
                  size="small"
                  onClick={moveMeasureDown}
                  disabled={!!lastMeasure}
                  margin="0 small"
                />
              </Tooltip>
            )}

            {(!extraData?.plan?.from_template ||
              assessment.type === CANVAS) && (
              <Tooltip
                renderTip={
                  assessment.deployed
                    ? t("assessment.manageAssessment")
                    : t("measures.edit")
                }
                on={["hover", "focus"]}
              >
                <IconButton
                  size="small"
                  renderIcon={IconEditLine}
                  screenReaderLabel={
                    assessment.deployed
                      ? t("assessment.manageAssessment")
                      : t("measures.edit")
                  }
                  onClick={() => setEditModal(true)}
                />
              </Tooltip>
            )}

            {!inTemplate && !assessment.deployed && (
              <Tooltip renderTip={t("measures.delete")} on={["hover", "focus"]}>
                <IconButton
                  color="danger"
                  size="small"
                  screenReaderLabel={t("measures.delete")}
                  renderIcon={IconTrashLine}
                  onClick={() => setDeleteModal(true)}
                  margin="0 0 0 small"
                />
              </Tooltip>
            )}
          </Flex.Item>
          <Flex.Item shouldShrink shouldGrow size="100%" textAlign="end">
            <Flex gap="small" wrap="wrap">
              {appliedTerms.map((term, idx) => (
                <Flex.Item key={idx}>
                  <Pill>{term.name}</Pill>
                </Flex.Item>
              ))}
            </Flex>
          </Flex.Item>
          {assessment.collecting_evidence && (
            <Flex.Item size="100%">
              <ToggleDetails
                summary={
                  <Text size="small" weight="bold" color="brand">
                    {t("assessment.manageJA")}
                  </Text>
                }
              >
                <JADetails
                  planId={extraData.plan?.id}
                  assessment={assessment}
                  handleSetFilesApprover={handleSetFilesApprover}
                  showTerms
                />
              </ToggleDetails>
            </Flex.Item>
          )}
          {assessment.assessment_type === "external" &&
            !assessment.collecting_evidence && (
              <Flex.Item size="100%">
                <ToggleDetails
                  summary={
                    <Text size="small" weight="bold" color="brand">
                      {t("assessment.manageExternal")}
                    </Text>
                  }
                >
                  <ExternalDetails
                    planId={extraData.plan?.id}
                    assessment={assessment}
                  />
                </ToggleDetails>
              </Flex.Item>
            )}
          <Flex.Item size="100%" margin="0 0 small 0">
            {assessment.description && (
              <Text as="div" size="small" lineHeight="double">
                <Text weight="bold" size="small">
                  {t("ratingScale.description")}:{" "}
                </Text>
                {assessment.description}
              </Text>
            )}
            {assessment.programs && assessment.programs.length > 0 && (
              <Text as="div" size="small" lineHeight="double">
                <Text weight="bold" size="small">
                  {t("assessment.programs")}:{" "}
                </Text>
                {assessment.programs.map((program, idx) =>
                  // Temporary degree name fix for program refactor
                  idx + 1 !== assessment.programs.length
                    ? (program.degree_name ?? program.name + ", ")
                    : (program.degree_name ?? program.name)
                )}
              </Text>
            )}
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.goal")}:
              </Text>{" "}
              {assessment.goal.description}
            </Text>
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.objective")}:
              </Text>{" "}
              {assessment.objective === null
                ? "None"
                : assessment.objective.description}
            </Text>
            <Text as="div" size="small" lineHeight="double">
              <Text weight="bold" size="small">
                {t("assessment.targetedLo")}:
              </Text>{" "}
              {sortOutcomes(relevantOutcomes).map((lo, idx) => {
                return idx !== 0 ? ", " + lo.name : lo.name;
              })}
            </Text>
          </Flex.Item>
        </Flex>
        <Flex alignItems="start" wrap="wrap" margin="0 0 small 0">
          <Flex.Item shouldGrow>
            <Text as="div" size="small" weight="bold">
              {t("assessment.departments")}
            </Text>

            {extraData.acSubs
              .filter((sub) => assessment.subjects.includes(sub.id))
              .map((sub, idx) => (
                <Text as="div" size="small" key={idx}>
                  {sub.title}
                </Text>
              ))}
          </Flex.Item>
          {assessment.course_levels.length > 0 && (
            <Flex.Item shouldGrow>
              <Text as="div" size="small" weight="bold">
                {t("assessment.levels")}
              </Text>
              {extraData.courseLevels
                .filter((clevel) =>
                  assessment.course_levels.includes(clevel.id)
                )
                .map((level, idx) => (
                  <Text as="div" size="small" key={idx}>
                    {`${t("programs.courseLevel")} ${level.courseLevel} (${
                      level.range.start
                    } - ${level.range.end})`}
                  </Text>
                ))}
            </Flex.Item>
          )}
          {assignmentType && (
            <Flex.Item>
              <Text as="div" size="small" weight="bold">
                {t("assessment.assignmentIs")}
              </Text>
              <Text size="small">{assignmentType.name}</Text>
            </Flex.Item>
          )}
        </Flex>
      </Card>
      <DialogYesNo
        message={t("assessment.deleteAssessmentConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={async () => {
          await handleDeleteMeasure(assessment, sectionIndex);
          setDeleteModal(false);
        }}
      />
      <MeasureForm
        openModal={editModal}
        setOpenModal={setEditModal}
        courseLevels={extraData.courseLevels}
        assignmentTypes={extraData.assignmentTypes}
        acSubs={extraData.acSubs}
        planId={extraData.plan?.id}
        allCycles={extraData.cycles}
        programs={extraData.programs}
        measure={assessment}
        inTemplate={inTemplate}
      />
    </>
  );
};

export default AssessmentCard;
